import { v4 as uuid } from 'uuid';
import { supabase } from './supabase';
import { GithubFormat } from '../pages/game/utils/formatCode';

// Storage constants
const STORAGE_BUCKET = 'playmix-assets';
const CODE_FOLDER = 'code';

/**
 * Generate a UUID for a new history entry
 */
export const generateHistoryEntryId = (): string => {
  return uuid();
};

/**
 * Upload game code to Supabase storage
 * @param historyEntryId The UUID of the history entry (must be pre-generated)
 * @param gameId The UUID of the game
 * @param code The code in GithubFormat
 * @returns The public URL of the uploaded code file
 */
export const uploadCodeToStorage = async (
  historyEntryId: string,
  gameId: string,
  code: GithubFormat
): Promise<string> => {
  const filePath = `${CODE_FOLDER}/${gameId}/${historyEntryId}.json`;
  const codeJson = JSON.stringify(code);
  const blob = new Blob([codeJson], { type: 'application/json' });

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(filePath, blob, {
      contentType: 'application/json',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading code to storage:', uploadError);
    throw new Error(`Failed to upload code: ${uploadError.message}`);
  }

  const { data: publicUrlData } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(filePath);

  return publicUrlData.publicUrl;
};

/**
 * Download game code from a storage URL
 * @param url The public URL of the code file
 * @returns The code in GithubFormat
 */
export const downloadCodeFromStorage = async (url: string, throwOnError = false): Promise<GithubFormat> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`[DOWNLOAD ERROR] Failed to fetch code: ${response.status} ${response.statusText}`);
    }
    const code = await response.json();
    if (!Array.isArray(code)) {
        // If it's valid JSON but not an array, we consider it invalid structure for our app
        const msg = `[DOWNLOAD ERROR] Code from URL is not a JSON array. URL: ${url}. Received type: ${typeof code}`;
        console.error(msg);
        throw new Error(msg);
    }
    return code as GithubFormat;
  } catch (error) {
    console.error('[DOWNLOAD ERROR] Error downloading code from storage:', error);
    if (throwOnError) {
      throw error;
    }
    return [];
  }
};
